import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import type { Product, ProductVariant, CartItem, Customer, TaxBreakdownItem } from '@/types/pos';
import { computeLineDiscountAmount } from '@/lib/line-discount';

interface CartState {
  items: CartItem[];
  customer: Customer | null;
  orderDiscountType: 'PERCENTAGE' | 'FIXED' | null;
  orderDiscountValue: number;
  notes: string;

  // Computed values
  subtotal: number;
  orderDiscountAmount: number;
  /** POS-I119: money given away on the lines (bargaining), for the cart total and the receipt "You saved". */
  lineDiscountTotal: number;
  totalTax: number;
  grandTotal: number;
  itemCount: number;
  /** Subtotal of items that ARE subject to payment method tax (skipPaymentMethodTax=false/undefined).
   *  Used by CheckoutDialog to compute the ICT-eligible base for 5%/15% rate. */
  pmTaxEligibleSubtotal: number;
  /** Subtotal of items that have their OWN embedded GST and skip payment method tax. */
  pmTaxExemptSubtotal: number;

  // Actions
  addItem: (product: Product, variant?: ProductVariant | null) => void;
  removeItem: (cartItemId: string) => void;
  updateQuantity: (cartItemId: string, quantity: number) => void;
  /** `meta` (POS-I119): why the line discount was given and the manager who approved it (audit trail). */
  setItemDiscount: (
    cartItemId: string,
    type: 'PERCENTAGE' | 'FIXED' | null,
    value: number,
    meta?: { reason?: string; approvedById?: string },
  ) => void;
  setItemPrice: (cartItemId: string, unitPrice: number) => void;
  setOrderDiscount: (type: 'PERCENTAGE' | 'FIXED' | null, value: number) => void;
  setCustomer: (customer: Customer | null) => void;
  setNotes: (notes: string) => void;
  setItemSerialNumber: (cartItemId: string, serialNumberId: string | null) => void;
  clearCart: () => void;

  // Internal
  recalculate: () => void;
}

/**
 * Resolve effective taxes for a product using the inheritance chain:
 * 1. Product-level override taxes (if taxOverride=true and taxes exist)
 * 2. Category-level taxes (inherited)
 * 3. Legacy single taxRate fallback
 */
function resolveEffectiveTaxes(product: Product): Array<{ name: string; rate: number; type: 'INCLUSIVE' | 'EXCLUSIVE' }> {
  if (product.taxOverride && product.taxes && product.taxes.length > 0) {
    return product.taxes
      .filter((pt) => pt.taxRate.isActive !== false)
      .map((pt) => ({ name: pt.taxRate.name, rate: Number(pt.taxRate.rate), type: pt.taxRate.type }));
  }

  if (product.category?.taxes && product.category.taxes.length > 0) {
    return product.category.taxes
      .filter((ct) => ct.taxRate.isActive !== false)
      .map((ct) => ({ name: ct.taxRate.name, rate: Number(ct.taxRate.rate), type: ct.taxRate.type }));
  }

  // Legacy fallback
  const taxRate = Number(product.taxRate);
  if (taxRate > 0) {
    return [{ name: 'Tax', rate: taxRate, type: product.taxInclusive ? 'INCLUSIVE' : 'EXCLUSIVE' }];
  }

  return [];
}

function calculateLineItem(
  product: Product,
  variant: ProductVariant | null,
  quantity: number,
  discountType: 'PERCENTAGE' | 'FIXED' | null,
  discountValue: number,
  overrideUnitPrice?: number, // when provided, used instead of product.salePrice
): Pick<CartItem, 'unitPrice' | 'discountAmount' | 'taxAmount' | 'taxBreakdown' | 'lineTotal'> {
  let unitPrice = overrideUnitPrice !== undefined ? overrideUnitPrice : Number(product.salePrice);
  if (variant) unitPrice += Number(variant.priceAdjustment);

  const lineSubtotalBeforeDiscount = unitPrice * quantity;

  // POS-I119: same owner maths as the server (rounded to 2 dp, capped at the line value).
  const discountAmount = computeLineDiscountAmount(lineSubtotalBeforeDiscount, discountType, discountValue);

  const lineSubtotal = lineSubtotalBeforeDiscount - discountAmount;
  const effectiveTaxes = resolveEffectiveTaxes(product);

  const taxBreakdown: TaxBreakdownItem[] = [];
  let totalTaxAmount = 0;

  for (const tax of effectiveTaxes) {
    let amount: number;
    if (tax.type === 'INCLUSIVE') {
      amount = lineSubtotal - lineSubtotal / (1 + tax.rate / 100);
    } else {
      amount = lineSubtotal * (tax.rate / 100);
    }
    amount = Math.round(amount * 100) / 100;
    taxBreakdown.push({ name: tax.name, rate: tax.rate, type: tax.type, amount });
    totalTaxAmount += amount;
  }

  // Line total: subtotal + exclusive taxes (inclusive taxes are already in the price)
  const exclusiveTaxTotal = taxBreakdown
    .filter((t) => t.type === 'EXCLUSIVE')
    .reduce((sum, t) => sum + t.amount, 0);
  const lineTotal = lineSubtotal + exclusiveTaxTotal;

  return {
    unitPrice,
    discountAmount: Math.round(discountAmount * 100) / 100,
    taxAmount: Math.round(totalTaxAmount * 100) / 100,
    taxBreakdown,
    lineTotal: Math.round(lineTotal * 100) / 100,
  };
}

export const useCartStore = create<CartState>((set, get) => ({
  items: [],
  customer: null,
  orderDiscountType: null,
  orderDiscountValue: 0,
  notes: '',
  subtotal: 0,
  orderDiscountAmount: 0,
  lineDiscountTotal: 0,
  totalTax: 0,
  grandTotal: 0,
  itemCount: 0,
  pmTaxEligibleSubtotal: 0,
  pmTaxExemptSubtotal: 0,

  addItem: (product, variant = null) => {
    const state = get();
    const isSerialTracked = !!product.serialTracking;

    // Derive baseSalePrice by reversing any active tier adjustment so we always
    // store the un-tiered base price. This lets repricing work correctly if the
    // customer (or tier) changes later.
    const activeTierPct = state.customer?.priceTier?.adjustmentPercent ?? 0;
    const rawSalePrice = Number(product.salePrice);
    const baseSalePrice = activeTierPct !== 0
      ? rawSalePrice / (1 + activeTierPct / 100)
      : rawSalePrice;

    const existingIdx = isSerialTracked
      ? -1
      : state.items.findIndex(
          (item) =>
            item.product.id === product.id &&
            (item.variant?.id || null) === (variant?.id || null),
        );

    let newItems: CartItem[];

    if (existingIdx >= 0) {
      newItems = [...state.items];
      const existing = newItems[existingIdx];
      const newQty = existing.quantity + 1;
      // Use the STORED product/variant so price stays locked for that line
      const calcs = calculateLineItem(
        existing.product, existing.variant, newQty,
        existing.discountType, existing.discountValue,
        existing.unitPrice, // keep exact same unit price
      );
      newItems[existingIdx] = { ...existing, quantity: newQty, ...calcs };
    } else {
      const calcs = calculateLineItem(product, variant, 1, null, 0, rawSalePrice);
      const newItem: CartItem = {
        id: uuidv4(),
        product,
        variant,
        quantity: 1,
        baseSalePrice,
        discountType: null,
        discountValue: 0,
        ...calcs,
      };
      newItems = [...state.items, newItem];
    }

    set({ items: newItems });
    get().recalculate();
  },

  removeItem: (cartItemId) => {
    set((s) => ({ items: s.items.filter((i) => i.id !== cartItemId) }));
    get().recalculate();
  },

  updateQuantity: (cartItemId, quantity) => {
    if (quantity <= 0) {
      get().removeItem(cartItemId);
      return;
    }
    set((s) => ({
      items: s.items.map((item) => {
        if (item.id !== cartItemId) return item;
        if (item.product.serialTracking) return item;
        const calcs = calculateLineItem(
          item.product, item.variant, quantity,
          item.discountType, item.discountValue,
          item.unitPrice,
        );
        return { ...item, quantity, ...calcs };
      }),
    }));
    get().recalculate();
  },

  setItemDiscount: (cartItemId, type, value, meta) => {
    set((s) => ({
      items: s.items.map((item) => {
        if (item.id !== cartItemId) return item;
        const calcs = calculateLineItem(
          item.product, item.variant, item.quantity, type, value,
          item.unitPrice,
        );
        // A removed (or zero) discount also clears its audit trail.
        const applied = !!type && calcs.discountAmount > 0;
        return {
          ...item,
          discountType: applied ? type : null,
          discountValue: applied ? value : 0,
          discountReason: applied ? meta?.reason?.trim() || undefined : undefined,
          discountApprovedById: applied ? meta?.approvedById : undefined,
          ...calcs,
        };
      }),
    }));
    get().recalculate();
  },

  setItemPrice: (cartItemId, unitPrice) => {
    set((s) => ({
      items: s.items.map((item) => {
        if (item.id !== cartItemId) return item;
        const calcs = calculateLineItem(
          item.product, item.variant, item.quantity,
          item.discountType, item.discountValue,
          unitPrice,
        );
        return { ...item, baseSalePrice: unitPrice, ...calcs };
      }),
    }));
    get().recalculate();
  },

  setOrderDiscount: (type, value) => {
    set({ orderDiscountType: type, orderDiscountValue: value });
    get().recalculate();
  },

  setCustomer: (customer) => {
    const state = get();
    const newAdjPct = customer?.priceTier?.adjustmentPercent ?? 0;

    // Reprice all existing cart items using their stored baseSalePrice
    const repriced = state.items.map((item) => {
      const newUnitPrice = newAdjPct !== 0
        ? item.baseSalePrice * (1 + newAdjPct / 100)
        : item.baseSalePrice;
      const calcs = calculateLineItem(
        item.product, item.variant, item.quantity,
        item.discountType, item.discountValue,
        newUnitPrice,
      );
      return { ...item, ...calcs };
    });

    set({ customer, items: repriced });
    get().recalculate();
  },
  setNotes: (notes) => set({ notes }),

  setItemSerialNumber: (cartItemId, serialNumberId) => {
    set((s) => ({
      items: s.items.map((item) =>
        item.id === cartItemId ? { ...item, serialNumberId: serialNumberId ?? undefined } : item,
      ),
    }));
  },

  clearCart: () =>
    set({
      items: [],
      customer: null,
      orderDiscountType: null,
      orderDiscountValue: 0,
      notes: '',
      subtotal: 0,
      orderDiscountAmount: 0,
      lineDiscountTotal: 0,
      totalTax: 0,
      grandTotal: 0,
      itemCount: 0,
      pmTaxEligibleSubtotal: 0,
      pmTaxExemptSubtotal: 0,
    }),

  recalculate: () => {
    const state = get();
    const subtotal = state.items.reduce((sum, i) => sum + i.unitPrice * i.quantity - i.discountAmount, 0);
    const totalTax = state.items.reduce((sum, i) => sum + i.taxAmount, 0);

    let orderDiscountAmount = 0;
    if (state.orderDiscountType && state.orderDiscountValue > 0) {
      if (state.orderDiscountType === 'PERCENTAGE') {
        orderDiscountAmount = subtotal * (state.orderDiscountValue / 100);
      } else {
        orderDiscountAmount = state.orderDiscountValue;
      }
    }

    const lineTotal = state.items.reduce((sum, i) => sum + i.lineTotal, 0);
    const grandTotal = Math.max(0, lineTotal - orderDiscountAmount);
    const itemCount = state.items.reduce((sum, i) => sum + i.quantity, 0);

    // Split subtotals for payment method tax calculation in checkout
    const pmTaxEligibleSubtotal = state.items
      .filter((i) => !i.product.skipPaymentMethodTax && !i.product.category?.skipPaymentMethodTax)
      .reduce((sum, i) => sum + i.unitPrice * i.quantity - i.discountAmount, 0);
    const pmTaxExemptSubtotal = state.items
      .filter((i) => i.product.skipPaymentMethodTax || i.product.category?.skipPaymentMethodTax)
      .reduce((sum, i) => sum + i.lineTotal, 0); // lineTotal includes their own embedded GST

    set({
      subtotal: Math.round(subtotal * 100) / 100,
      orderDiscountAmount: Math.round(orderDiscountAmount * 100) / 100,
      lineDiscountTotal: Math.round(state.items.reduce((sum, i) => sum + i.discountAmount, 0) * 100) / 100,
      totalTax: Math.round(totalTax * 100) / 100,
      grandTotal: Math.round(grandTotal * 100) / 100,
      itemCount,
      pmTaxEligibleSubtotal: Math.round(pmTaxEligibleSubtotal * 100) / 100,
      pmTaxExemptSubtotal: Math.round(pmTaxExemptSubtotal * 100) / 100,
    });
  },
}));
